import type { AssetListItem, AssetType } from '@cartwhl/common/api/getAssets';

import { formatEditedAt } from '../Dashboard/workItemGrouping';
import { getMogenJobDisplayTypeFromGroupID } from './assetLibraryGroupIds';
import type { AssetLibraryFilters, AssetLibraryTypeFilter } from './assetLibraryFilters';
import { assetMatchesTypeFilter } from './assetLibraryFilters';
import type { AssetLibraryEmbedConfig } from './assetLibraryEmbedConfig';
import type { AssetLibraryView } from './assetLibraryView';
import type { OrganizationPlanType } from '@cartwhl/common/types';
import isLegacyOrganizationPlan from '@cartwhl/common/utils/isLegacyOrganizationPlan';

export function isTeamPlan(
  planType: OrganizationPlanType | undefined
): boolean {
  if (!planType ||
    isLegacyOrganizationPlan(planType) ||
    planType === 'individual_basic' ||
    planType === 'individual_pro') {
    return false;
  }

  return true;
}

export function getAssetLibraryTitle(
  planType: OrganizationPlanType | undefined,
): 'My Library' | 'Team Library' {
  return isTeamPlan(planType) ? 'Team Library' : 'My Library';
}

export function getAssetLibraryCaption(
  planType: OrganizationPlanType | undefined,
): string {
  return isTeamPlan(planType)
    ? 'Everything you and your team creates and uploads can be found here.'
    : 'Everything you create and upload can be found here.';
}

export type AssetLibraryDisplayItem = AssetListItem & {
  groupItemCount?: number;
};

export function getAssetDisplayType(asset: AssetListItem): string {
  switch (asset.assetType) {
    case 'MEDIA_IMAGE':
      return 'Image';
    case 'MEDIA_VIDEO':
      return 'Video';
    case 'SCENE':
      return 'Cartwheel scene';
    case 'MASCOT_JOB':
      return 'Mascot job';
    case 'MOGEN_JOB':
      return getMogenJobDisplayTypeFromGroupID(asset.groupID);
    default:
      return 'Asset';
  }
}

export function getAssetCardTitle(asset: AssetLibraryDisplayItem): string {
  if (shouldShowStackedPreview(asset) && asset.groupName?.trim()) {
    return asset.groupName.trim();
  }

  return asset.assetName;
}

export function getAssetCardMetadata(asset: AssetLibraryDisplayItem): string {
  const kindLabel = getAssetDisplayType(asset);

  if (!asset.groupItemCount || asset.groupItemCount <= 1) {
    return kindLabel;
  }

  const itemLabel = asset.groupItemCount === 1 ? 'item' : 'items';

  return `${kindLabel} — ${asset.groupItemCount} ${itemLabel}`;
}

const MOGEN_SUBTYPE_FILTERS = new Set<AssetLibraryTypeFilter>([
  'canvas-project',
  'comic-batch',
  'swing-batch',
]);

function assetMatchesLibraryView(
  asset: AssetListItem,
  libraryView: AssetLibraryView,
): boolean {
  if (libraryView === 'bookmarks') {
    return !asset.isDeleted;
  }

  if (libraryView === 'trash') {
    return Boolean(asset.isDeleted);
  }

  return !asset.isDeleted;
}

export function filterAssetsForDisplay(
  assets: AssetListItem[],
  filters: Pick<AssetLibraryFilters, 'typeFilter' | 'searchQuery'>,
  embedConfig?: AssetLibraryEmbedConfig | null,
  sortAscending = false,
  libraryView: AssetLibraryView = 'all',
): AssetLibraryDisplayItem[] {
  let filteredAssets = assets.filter((asset) =>
    assetMatchesLibraryView(asset, libraryView),
  );

  if (embedConfig?.allowedAssetTypes.length) {
    const allowedAssetTypes = new Set(embedConfig.allowedAssetTypes);
    filteredAssets = filteredAssets.filter((asset) =>
      allowedAssetTypes.has(asset.assetType as AssetType),
    );
  }

  if (filters.typeFilter !== 'all') {
    if (MOGEN_SUBTYPE_FILTERS.has(filters.typeFilter)) {
      filteredAssets = filteredAssets.filter((asset) =>
        assetMatchesTypeFilter(asset, filters.typeFilter),
      );
    }
  }

  const normalizedSearchQuery = filters.searchQuery.trim().toLowerCase();

  if (normalizedSearchQuery) {
    const matchingGroupIDs = new Set<string>();

    for (const asset of filteredAssets) {
      if (
        shouldGroupAssetForDisplay(asset) &&
        assetMatchesSearchQuery(asset, normalizedSearchQuery)
      ) {
        matchingGroupIDs.add(asset.groupID!);
      }
    }

    filteredAssets = filteredAssets.filter((asset) => {
      if (shouldGroupAssetForDisplay(asset)) {
        return matchingGroupIDs.has(asset.groupID!);
      }

      return assetMatchesSearchQuery(asset, normalizedSearchQuery);
    });
  }

  return groupAssetsForDisplay(filteredAssets, sortAscending);
}

function compareAssetsByLastUpdated(
  left: AssetListItem,
  right: AssetListItem,
  sortAscending: boolean,
): number {
  const diff =
    new Date(left.lastUpdatedAt).getTime() -
    new Date(right.lastUpdatedAt).getTime();

  return sortAscending ? diff : -diff;
}

function assetMatchesSearchQuery(
  asset: AssetListItem,
  normalizedSearchQuery: string,
): boolean {
  return [asset.assetName, asset.groupName]
    .filter((name): name is string => Boolean(name?.trim()))
    .some((name) => name.toLowerCase().includes(normalizedSearchQuery));
}

function shouldGroupAssetForDisplay(asset: AssetListItem): boolean {
  return Boolean(asset.groupID);
}

function countAssetsByGroupID(assets: AssetListItem[]): Map<string, number> {
  const groupCounts = new Map<string, number>();

  for (const asset of assets) {
    if (!shouldGroupAssetForDisplay(asset)) {
      continue;
    }

    groupCounts.set(asset.groupID!, (groupCounts.get(asset.groupID!) ?? 0) + 1);
  }

  return groupCounts;
}

export function groupAssetsForDisplay(
  assets: AssetListItem[],
  sortAscending = false,
): AssetLibraryDisplayItem[] {
  const sortedAssets = [...assets].sort((left, right) =>
    compareAssetsByLastUpdated(left, right, sortAscending),
  );
  const groupCounts = countAssetsByGroupID(assets);
  const seenGroupIDs = new Set<string>();
  const displayAssets: AssetLibraryDisplayItem[] = [];

  for (const asset of sortedAssets) {
    if (!shouldGroupAssetForDisplay(asset)) {
      displayAssets.push(asset);
      continue;
    }

    if (seenGroupIDs.has(asset.groupID!)) {
      continue;
    }

    seenGroupIDs.add(asset.groupID!);
    const groupItemCount = groupCounts.get(asset.groupID!);

    displayAssets.push({
      ...asset,
      groupItemCount: groupItemCount && groupItemCount > 1 ? groupItemCount : undefined,
    });
  }

  return displayAssets;
}

export function getAssetLibraryItemKey(asset: AssetListItem): string {
  if (shouldGroupAssetForDisplay(asset)) {
    return asset.groupID!;
  }

  return asset.assetID;
}

export function shouldShowStackedPreview(asset: AssetLibraryDisplayItem): boolean {
  return (asset.groupItemCount ?? 0) > 1;
}

export function isMainLibraryPageItem(
  embedded: boolean,
  libraryView: AssetLibraryView,
): boolean {
  return !embedded && libraryView === 'all';
}

export function isCardBookmarkVisible(libraryView: AssetLibraryView): boolean {
  return libraryView === 'all' || libraryView === 'bookmarks';
}

export function isCardMoveToTrashVisible(libraryView: AssetLibraryView): boolean {
  return libraryView === 'all' || libraryView === 'bookmarks';
}

export function getUrlPathname(url: string): string | null {
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

/** Skip swapping media src when only the presigned query string changed. */
export function shouldUpdateStableMediaSrc(
  currentSrc: string,
  candidateUrl: string,
  hasLoadError: boolean,
): boolean {
  if (!candidateUrl) {
    return false;
  }

  if (currentSrc === candidateUrl) {
    return false;
  }

  const nextPath = getUrlPathname(candidateUrl);
  const currentPath = currentSrc ? getUrlPathname(currentSrc) : null;
  const sameObjectPath =
    nextPath !== null && currentPath !== null && nextPath === currentPath;

  return !(sameObjectPath && !hasLoadError);
}

function isImagePreviewUrl(url: string): boolean {
  return /\.(jpe?g|png|webp|gif)(?:[?#]|$)/i.test(url);
}

export function isAssetVideoThumbnail(
  assetType: AssetType,
  url: string | null | undefined,
): boolean {
  if (assetType === 'MASCOT_JOB') {
    return true;
  }

  const normalized = url?.trim() ?? '';
  if (!normalized) {
    return assetType === 'MEDIA_VIDEO';
  }

  if (normalized.startsWith('blob:')) {
    return assetType !== 'MEDIA_IMAGE';
  }

  if (isImagePreviewUrl(normalized)) {
    return false;
  }

  if (assetType === 'MEDIA_VIDEO') {
    return (
      normalized.includes('thumbnail.mp4') ||
      /\.(mp4|webm|mov)(?:[?#]|$)/i.test(normalized)
    );
  }

  return (
    normalized.includes('thumbnail.mp4') || /\.mp4(?:[?#]|$)/i.test(normalized)
  );
}

export function formatAssetUpdatedLabel(lastUpdatedAt: string): string {
  return `Edited ${formatEditedAt(lastUpdatedAt)}`;
}
